import Image, { ImageProps, StaticImageData } from 'next/image';
import { cva, VariantProps } from 'class-variance-authority';
import { forwardRef, HTMLAttributes } from 'react';

import { mergeClassNames } from '@lib/utils';

const avatarVariants = cva(
  'relative flex shrink-0 overflow-hidden rounded-full',
  {
    variants: {
      size: {
        default: 'size-10 min-w-10 min-h-10',
        sm: 'size-8 min-w-8 min-h-8',
        lg: 'size-16 min-w-16 min-h-16',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

interface AvatarProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof avatarVariants> {}

const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, size, ...props }, ref) => (
    <div
      ref={ref}
      {...props}
      className={mergeClassNames(avatarVariants({ size, className }))}
    />
  ),
);

Avatar.displayName = 'Avatar';

interface AvatarImageProps
  extends Omit<ImageProps, 'src' | 'fill' | 'width' | 'height'> {
  src?: string | StaticImageData;
}

const AvatarImage = forwardRef<HTMLImageElement, AvatarImageProps>(
  ({ className, src, alt = 'User Avatar', ...props }, ref) => {
    if (!src) return null;

    return (
      <Image
        ref={ref}
        src={src}
        alt={alt}
        {...props}
        fill
        className={mergeClassNames('aspect-square size-full', className)}
      />
    );
  },
);

AvatarImage.displayName = 'AvatarImage';

const AvatarFallback = forwardRef<
  HTMLSpanElement,
  HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    {...props}
    className={mergeClassNames(
      'flex-center size-full rounded-full bg-gray-500 text-white text-xs font-medium',
      className,
    )}
  />
));

AvatarFallback.displayName = 'AvatarFallback';

export { Avatar, AvatarImage, AvatarFallback };
